import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./DashboardQari.css";
import NavbarAll from "../Navbar/NavbarAll";

function getInitial(nameOrEmail) {
  const s = (nameOrEmail || "T").trim();
  return s.charAt(0).toUpperCase();
}

function DashboardQari({ user, onLogout }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/hifz-login", { replace: true });
  };
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [createdStudent, setCreatedStudent] = useState(null);
  const [copiedField, setCopiedField] = useState("");
  const [studentToRemove, setStudentToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const token = localStorage.getItem("token");
  const apiURL = process.env.REACT_APP_API_URL;

  const teacherEmail = user?.email || "";
  const teacherLabel = teacherEmail ? teacherEmail.split("@")[0] : "Teacher";

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (!showAddModal) return;
    const onEscape = (e) => {
      if (e.key === "Escape") closeAddModal();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEscape);
    };
  }, [showAddModal]);

  useEffect(() => {
    if (!openMenuId) return;
    const closeMenu = () => setOpenMenuId(null);
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, [openMenuId]);

  useEffect(() => {
    if (!createdStudent) return;
    const onEscape = (e) => {
      if (e.key === "Escape") closeCreatedModal();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEscape);
    };
  }, [createdStudent]);

  useEffect(() => {
    if (!studentToRemove) return;
    const onEscape = (e) => {
      if (e.key === "Escape" && !removing) closeRemoveModal();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEscape);
    };
  }, [studentToRemove, removing]);

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${apiURL}/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(res.data);
      setFilteredStudents(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleStudentClick = (student) => {
    navigate(`/dashboard-qari/students/${student.id}`, {
      state: { student, role: "teacher" },
    });
  };

  const goToStudentProfile = (e, student) => {
    e.stopPropagation();
    setOpenMenuId(null);
    navigate(`/dashboard-qari/students/${student.id}/profile`, {
      state: { student, from: "dashboard-qari" },
    });
  };

  const openAddModal = () => {
    setStudentName("");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setStudentName("");
  };

  const createStudent = async (e) => {
    e.preventDefault();
    const name = studentName.trim();
    if (!name) {
      alert("Please enter the student name");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${apiURL}/students`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeAddModal();
      fetchStudents();
      setCreatedStudent({
        name,
        email: res.data.email || "",
        password: "123456",
        portalLink: res.data.deepLink || "",
      });
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to add student");
    } finally {
      setSubmitting(false);
    }
  };

  const copyValue = (field, value) => {
    if (!value) return;
    const done = () => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done).catch(() => {});
    } else {
      done();
    }
  };

  const copyAllDetails = () => {
    if (!createdStudent) return;
    const text =
      `Name: ${createdStudent.name}\n` +
      `Email: ${createdStudent.email}\n` +
      `Password: ${createdStudent.password}\n` +
      `Portal link: ${createdStudent.portalLink}`;
    copyValue("all", text);
  };

  const closeCreatedModal = () => {
    setCreatedStudent(null);
    setCopiedField("");
  };

  const toggleStudentMenu = (e, studentId) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === studentId ? null : studentId));
  };

  const openRemoveModal = (e, student) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setRemoveError("");
    setStudentToRemove(student);
  };

  const closeRemoveModal = () => {
    if (removing) return;
    setStudentToRemove(null);
    setRemoveError("");
  };

  const confirmRemoveStudent = async () => {
    if (!studentToRemove || removing) return;

    setRemoving(true);
    setRemoveError("");
    try {
      await axios.delete(`${apiURL}/users/${studentToRemove.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeRemoveModal();
      fetchStudents();
    } catch (err) {
      console.error(err);
      setRemoveError(err.response?.data?.error || "Failed to remove student");
    } finally {
      setRemoving(false);
    }
  };

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = students.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term) ||
        s.id?.toString().includes(term)
    );
    setFilteredStudents(filtered);
  }, [searchTerm, students]);

  if (loading) {
    return (
      <>
        <NavbarAll />
        <div className="qari-dashboard-wrapper">
          <div className="qari-dashboard-loading">Loading your class…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavbarAll />
      <div className="qari-dashboard-wrapper">
        <div className="qari-dashboard-topbar" role="banner">
          <div className="qari-topbar-left">
            <div className="qari-header-dots" aria-hidden="true">
              <span /><span /><span />
            </div>
            <div className="qari-topbar-titles">
              <h1 className="qari-dashboard-title">Teacher Dashboard</h1>
              <p className="qari-dashboard-subtitle">Hifz Portal · My Class</p>
            </div>
          </div>
          <div className="qari-topbar-teacher">
            <div className="qari-avatar" aria-hidden="true">
              {getInitial(teacherLabel)}
            </div>
            <span className="qari-teacher-name" title={teacherEmail}>
              {teacherLabel}
            </span>
            <button
              type="button"
              className="hifz-logout-btn"
              onClick={handleLogout}
              aria-label="Log out"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span className="hifz-logout-btn-text">Logout</span>
            </button>
          </div>
        </div>

        <main className="qari-dashboard">
          <section className="qari-stats-row" aria-label="Class summary">
            <div className="qari-stat-card">
              <span className="qari-stat-value">{students.length}</span>
              <span className="qari-stat-label">Students in class</span>
            </div>
            <div className="qari-stat-card qari-stat-card-accent">
              <span className="qari-stat-value">
                {searchTerm ? filteredStudents.length : students.length}
              </span>
              <span className="qari-stat-label">
                {searchTerm ? "Search results" : "Showing all"}
              </span>
            </div>
          </section>

          <section className="qari-toolbar">
            <div className="qari-search-wrap">
              <svg
                className="qari-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                className="qari-search-input"
                placeholder="Search by name or email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button type="button" className="qari-add-btn" onClick={openAddModal}>
              <span className="qari-add-btn-icon" aria-hidden="true">+</span>
              Add Student
            </button>
          </section>

          <section className="qari-student-list">
            {filteredStudents.length > 0 ? (
              filteredStudents.map((s) => (
                <article
                  key={s.id}
                  className="qari-student-card"
                  onClick={() => handleStudentClick(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleStudentClick(s);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="qari-student-card-main">
                    <div className="qari-student-avatar" aria-hidden="true">
                      {getInitial(s.name)}
                    </div>
                    <div className="qari-student-info">
                      <h3 className="qari-student-name">{s.name}</h3>
                      <p className="qari-student-email">{s.email}</p>
                    </div>
                    <div
                      className="qari-card-menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="qari-menu-trigger"
                        onClick={(e) => toggleStudentMenu(e, s.id)}
                        aria-label={`Options for ${s.name}`}
                        aria-expanded={openMenuId === s.id}
                        aria-haspopup="menu"
                      >
                        <span /><span /><span />
                      </button>
                      {openMenuId === s.id && (
                        <div className="qari-menu-dropdown" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            className="qari-menu-item"
                            onClick={(e) => goToStudentProfile(e, s)}
                          >
                            View profile
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="qari-menu-item qari-menu-item-danger"
                            onClick={(e) => openRemoveModal(e, s)}
                          >
                            Remove student
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="qari-student-card-footer">
                    <span className="qari-view-progress">View progress →</span>
                  </div>
                </article>
              ))
            ) : students.length === 0 ? (
              <div className="qari-empty-state">
                <div className="qari-empty-icon" aria-hidden="true">📚</div>
                <h3>No students yet</h3>
                <p>Add your first student to start tracking Hifz progress.</p>
                <button type="button" className="qari-add-btn qari-add-btn-large" onClick={openAddModal}>
                  + Add Student to Class
                </button>
              </div>
            ) : (
              <div className="qari-empty-state qari-empty-state-muted">
                <p>No students match your search.</p>
              </div>
            )}
          </section>
        </main>
      </div>

      {showAddModal &&
        createPortal(
          <div
            className="hifz-add-student-overlay"
            onClick={closeAddModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-student-title"
          >
            <div className="hifz-add-student-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="hifz-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                ×
              </button>
              <h3 id="add-student-title">Add Student to Class</h3>
              <p className="add-student-hint">
                Email is auto-created (e.g. <strong>name.surname@zakariamasjid.com</strong>).
                Default password: <strong>123456</strong>
              </p>
              <form onSubmit={createStudent}>
                <label htmlFor="student-name">Student name</label>
                <input
                  id="student-name"
                  type="text"
                  placeholder="e.g. Ahmad Khan"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  autoFocus
                  required
                  disabled={submitting}
                />
                <div className="add-student-actions">
                  <button
                    type="button"
                    className="cancel-add-btn"
                    onClick={closeAddModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="submit-add-btn" disabled={submitting}>
                    {submitting ? "Adding…" : "Add Student"}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {createdStudent &&
        createPortal(
          <div
            className="hifz-add-student-overlay"
            onClick={closeCreatedModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="created-student-title"
          >
            <div
              className="hifz-add-student-modal hifz-created-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="hifz-modal-close"
                onClick={closeCreatedModal}
                aria-label="Close"
              >
                ×
              </button>

              <div className="hifz-created-check" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              <h3 id="created-student-title">Student added successfully</h3>
              <p className="add-student-hint">
                Share these login details with the student. Please keep them safe.
              </p>

              <div className="hifz-cred-list">
                {[
                  { key: "name", label: "Name", value: createdStudent.name },
                  { key: "email", label: "Email", value: createdStudent.email },
                  { key: "password", label: "Password", value: createdStudent.password },
                  { key: "portalLink", label: "Portal link", value: createdStudent.portalLink },
                ].map((field) => (
                  <div className="hifz-cred-row" key={field.key}>
                    <div className="hifz-cred-info">
                      <span className="hifz-cred-label">{field.label}</span>
                      <span className="hifz-cred-value">{field.value || "—"}</span>
                    </div>
                    <button
                      type="button"
                      className="hifz-cred-copy"
                      onClick={() => copyValue(field.key, field.value)}
                      disabled={!field.value}
                      aria-label={`Copy ${field.label}`}
                    >
                      {copiedField === field.key ? "Copied" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>

              <div className="add-student-actions">
                <button type="button" className="cancel-add-btn" onClick={closeCreatedModal}>
                  Close
                </button>
                <button type="button" className="submit-add-btn" onClick={copyAllDetails}>
                  {copiedField === "all" ? "Copied!" : "Copy all details"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {studentToRemove &&
        createPortal(
          <div
            className="hifz-add-student-overlay"
            onClick={closeRemoveModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-student-title"
          >
            <div
              className="hifz-add-student-modal hifz-remove-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="hifz-modal-close"
                onClick={closeRemoveModal}
                aria-label="Close"
                disabled={removing}
              >
                ×
              </button>

              <div className="hifz-remove-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </div>

              <h3 id="remove-student-title">Remove student?</h3>
              <p className="add-student-hint">
                This will permanently remove <strong>{studentToRemove.name}</strong> from your class,
                including their login and all progress records. This cannot be undone.
              </p>

              <div className="hifz-remove-details">
                <div className="hifz-remove-detail-row">
                  <span className="hifz-remove-detail-label">Name</span>
                  <span className="hifz-remove-detail-value">{studentToRemove.name}</span>
                </div>
                {studentToRemove.email && (
                  <div className="hifz-remove-detail-row">
                    <span className="hifz-remove-detail-label">Email</span>
                    <span className="hifz-remove-detail-value">{studentToRemove.email}</span>
                  </div>
                )}
              </div>

              {removeError && (
                <div className="hifz-remove-error" role="alert">
                  {removeError}
                </div>
              )}

              <div className="add-student-actions">
                <button
                  type="button"
                  className="cancel-add-btn"
                  onClick={closeRemoveModal}
                  disabled={removing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="hifz-remove-confirm-btn"
                  onClick={confirmRemoveStudent}
                  disabled={removing}
                >
                  {removing ? "Removing…" : "Remove student"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default DashboardQari;
